'use client';

import React from 'react';
import dynamic from 'next/dynamic';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import PinIconAlt from '@repo/ui/src/components/Icons/PinIconAlt';

export interface LocallyConfig {
  companyId: string | number;
  companyName: string;
  domain: string;
}

export interface LocallyDropdownProps {
  upc: string;
  config: LocallyConfig;
  className?: string;
  nDealers?: number;
}

// Convert React.useId() string to a numeric hash for Locally.com API
function hashIdToNumber(id: string): number {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    const char = id.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return Math.abs(hash);
}

export const LocallyDropdownInner = ({
  upc,
  config,
  className,
  nDealers = 3,
}: LocallyDropdownProps) => {
  const { t } = useTranslation();
  const { geo } = useCurrentUrl();
  const lang = geo?.locale?.toLowerCase() || 'en-US';

  const reactId = React.useId();
  const instanceId = React.useMemo(() => hashIdToNumber(reactId), [reactId]);

  const [locationName, setLocationName] = React.useState<string | null>(null);

  React.useEffect(() => {
    const handleLocallyUpdate = (e: any) => {
      const detail = e?.detail || {};
      if (detail.location_name) {
        setLocationName(detail.location_name);
      }
    };
    window.addEventListener('LOCALLY_data_update', handleLocallyUpdate);
    return () => {
      window.removeEventListener('LOCALLY_data_update', handleLocallyUpdate);
    };
  }, []);

  const openLocallyLocationModal = React.useCallback(() => {
    const el = document.querySelector(`#lcly-location-prompt-link-${instanceId}`);
    if (el && typeof (el as any).click === 'function') {
      (el as any).click();
    }
  }, [instanceId]);

  const generateConfig = React.useCallback(() => {
    const isCanada = geo?.geoLoc?.includes('ca');
    return {
      id: String(instanceId),
      company_name: config.companyName,
      button_text: t('productDetailPageContent.locallyButtonText', undefined, {
        default: 'Shop Local',
      }),
      button_id: 'HTML',
      company_id: String(config.companyId),
      always_hide_button: '1',
      css: '5',
      upc,
      show_location_switcher: '1',
      show_location_prompt: '1',
      lang,
      show_dealers: '1',
      n_dealers: String(nDealers),
      show_dealer_count: '1',
      show_address: '1',
      link_opens_modal: '1',
      radius_in_miles: '25',
      no_link: '0',
      ...(isCanada && { only_show_country: 'CA' }),
    };
  }, [instanceId, config, t, upc, lang, geo?.geoLoc, nDealers]);

  const generateUrl = React.useCallback((cfg: Record<string, any>) => {
    return new URLSearchParams(
      Object.entries(cfg).map(([key, value]) => [
        key,
        typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value ?? ''),
      ]),
    );
  }, []);

  const injectScript = React.useCallback((src: string, scriptId: string) => {
    const existingScript = document.getElementById(scriptId);
    if (!existingScript) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = src;
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  React.useEffect(() => {
    const cfg = generateConfig();
    const url = generateUrl(cfg);
    const scriptId = `lcly-script-${instanceId}`;

    injectScript(`https://${config.domain}/stores/map.js?${url}`, scriptId);
    injectScript(
      `https://${config.domain}/switchlive/switchlive-events.js?company_id=${config.companyId}`,
      `lcly-script-switchlive-${instanceId}`,
    );
    injectScript(
      `https://${config.domain}/js/switchlive.js?company_id=${config.companyId}`,
      `lcly-script-switchlive-events-${instanceId}`,
    );
  }, [generateConfig, generateUrl, injectScript, upc, lang, instanceId, config]);

  if (!upc) {
    return null;
  }

  return (
    <div className={`locally-dropdown-style ${className || ''}`}>
      <style>{'.lcly-anchor { cursor: pointer; }'}</style>
      <div className="flex justify-between">
        <span className="tra-text-web-utl-button-l">
          {t('productDetailPageContent.shopLocally', undefined, {
            default: 'SHOP LOCALLY',
          })}
        </span>
        <button
          type="button"
          aria-label={t('productDetailPageContent.changeLocation', undefined, {
            default: 'Change location',
          })}
          className="flex tra-text-web-utl-button-l gap-2 text-brand-300 cursor-pointer items-center bg-transparent border-0 p-0"
          onClick={openLocallyLocationModal}
        >
          <PinIconAlt />
          {locationName
            ? locationName.toUpperCase()
            : t('productDetailPageContent.loading', undefined, { default: 'LOADING...' })}
        </button>
      </div>

      <div
        id={`lcly-button-${instanceId}`}
        data-switchlive="true"
        data-switchlive-impression="true"
        data-switchlive-impression-id-pl="1"
        className={`locally-dropdown-style ${className || ''}`}
      >
        <span
          id={`lcly-link-${instanceId}`}
          data-switchlive="true"
          data-switchlive-mode="auto"
          data-switchlive-id-pl="6"
          className="lcly-fallback-text"
        >
          Product Locator by Locally
        </span>
      </div>
    </div>
  );
};

const LocallyDropdown = dynamic(() => Promise.resolve(LocallyDropdownInner), { ssr: false });

export default LocallyDropdown;
